// v77.47 — "Your children" Settings card.
//
// Groups every child the user has access to by their role for that
// child:
//   • Managed by you   — children where you're the primary/managing parent
//   • Co-parenting      — children someone else manages, you're linked to
//
// Each row shows: child colour dot + name, role badge, a tiny strip
// of OTHER parents (chips with their parent_color), and a tooltip-y
// "managed by …" line for the co-parenting list. Quietly self-hides
// for users who haven't onboarded yet (zero children).
//
// v77.48 — Surfaces the household identity so blended-family users
// can see at a glance which household their session is anchored to
// (the household_id is the bridge between Charlie + Daisy in the
// example above; without this chip, users have no front-end way to
// confirm "yes, I'm in Alice's household even when I'm managing my
// own child Daisy").

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, ChevronRight, ShieldCheck, Home } from "lucide-react";

export function YourChildrenCard() {
  const { household, user } = useAuth();
  const [data, setData] = useState(null); // { managed, coparenting } | null

  useEffect(() => {
    let alive = true;
    api.get("/me/child-roles")
      .then((r) => { if (alive) setData(r.data || { managed: [], coparenting: [] }); })
      .catch(() => { if (alive) setData({ managed: [], coparenting: [] }); });
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const { managed = [], coparenting = [] } = data;
  const total = managed.length + coparenting.length;
  if (total === 0) return null;

  const renderOthers = (others) => {
    if (!others?.length) {
      return (
        <span className="text-xs text-[#6F828A] italic">
          No co-parent linked yet.
        </span>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {others.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-sm border bg-white"
            style={{ borderColor: p.parent_color || "#D9E1E3" }}
            title={p.email}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: p.parent_color || "#1F9FA4" }}
            />
            <span className="font-medium text-[#2E3A3F]">{p.name}</span>
            {p.is_primary_parent && (
              <span className="text-[10px] uppercase tracking-[0.05em] text-[#1F9FA4]">
                Primary
              </span>
            )}
          </span>
        ))}
      </div>
    );
  };

  return (
    <section
      className="border border-[#D9E1E3] bg-white rounded-md p-6"
      data-testid="your-children-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-medium text-[#2E3A3F] inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1F9FA4]" strokeWidth={1.75} /> Your children
          </h2>
          <p className="text-sm text-[#6F828A] mt-1">
            Children grouped by your role. Different roles per child are fine — you can manage one child and co-parent on another.
          </p>
        </div>
        <Link
          to="/app/children"
          className="inline-flex items-center gap-1 text-sm text-[#1F9FA4] hover:text-[#178085] font-medium flex-shrink-0"
          data-testid="your-children-manage-link"
        >
          Open Children <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* v77.48 — Household context chip. Helps blended-family users
          confirm which household they're anchored to. Two roles can
          coexist (managing one child, co-parenting another) but
          there's still ONE household_id behind the session. */}
      {household && (
        <div
          className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-sm border border-[#D9E1E3] bg-[#FAFBFB] text-[#2E3A3F]"
          data-testid="your-children-household-chip"
        >
          <Home className="w-3.5 h-3.5 text-[#1F9FA4]" strokeWidth={1.75} />
          <span className="font-medium" data-testid="your-children-household-name">
            {household.name || "Your household"}
          </span>
          {household.id && (
            <span
              className="font-mono-data text-[10px] text-[#6F828A] uppercase tracking-wider"
              title={`Household ID: ${household.id}`}
              data-testid="your-children-household-id"
            >
              · {String(household.id).slice(0, 8)}
            </span>
          )}
          {user?.is_primary_parent && (
            <span className="text-[10px] uppercase tracking-[0.05em] text-[#1F6A47] bg-[#E0F0E6] border border-[#C8E0D4] rounded-sm px-1.5 py-0.5">
              Household creator
            </span>
          )}
        </div>
      )}

      {/* Managed by you */}
      {managed.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
            Managed by you ({managed.length})
          </div>
          <ul className="divide-y divide-[#EEF3F4] border border-[#EEF3F4] rounded-sm">
            {managed.map((c) => (
              <li
                key={c.id}
                className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                data-testid={`your-children-managed-${c.id}`}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                  <span className="font-medium text-[#2E3A3F] truncate">{c.name}</span>
                  <span className="inline-flex items-center text-[10px] uppercase tracking-[0.05em] font-semibold text-[#116B6E] bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm px-1.5 py-0.5 flex-shrink-0">
                    Primary
                  </span>
                </div>
                <div className="sm:text-right">{renderOthers(c.other_parents)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Co-parenting */}
      {coparenting.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
            Co-parenting ({coparenting.length})
          </div>
          <ul className="divide-y divide-[#EEF3F4] border border-[#EEF3F4] rounded-sm">
            {coparenting.map((c) => {
              const managing = c.managing_parent;
              return (
                <li
                  key={c.id}
                  className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  data-testid={`your-children-coparent-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                      <span className="font-medium text-[#2E3A3F] truncate">{c.name}</span>
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.05em] font-semibold text-[#5C5C5C] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5 flex-shrink-0">
                        Co-parent
                      </span>
                    </div>
                    {managing && (
                      <div className="text-xs text-[#6F828A] mt-0.5 truncate">
                        Managed by <span className="font-medium text-[#2E3A3F]">{managing.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="sm:text-right">{renderOthers(c.other_parents)}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

export default YourChildrenCard;
